import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/admin-supabase';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  DollarSign,
  FileText,
  Download,
  Tag,
  Share2,
  ChevronRight
} from 'lucide-react';

interface Emenda {
  id: string;
  titulo: string;
  resumo?: string;
  descricao_completa?: string;
  valor_emenda?: number;
  imagem_capa?: string;
  categoria?: string;
  municipio_beneficiado?: string;
  estado?: string;
  created_at: string;
}

interface Documento {
  id: string;
  nome_documento: string;
  arquivo_url: string;
}

const EmendaDetalhes = () => {
  const { id } = useParams<{ id: string }>();
  const [emenda, setEmenda] = useState<Emenda | null>(null);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetalhes = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // Buscar dados da emenda
        const { data: emendaData, error: emendaError } = await supabase
          .from('emendas')
          .select('*')
          .eq('id', id)
          .single();

        if (emendaError) throw emendaError;
        setEmenda(emendaData);

        // Buscar documentos relacionados
        const { data: docsData, error: docsError } = await supabase
          .from('emenda_documentos')
          .select('*')
          .eq('emenda_id', id);

        if (!docsError) setDocumentos(docsData || []);

      } catch (err) {
        console.error('Erro ao buscar detalhes da emenda:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetalhes();
  }, [id]);

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!emenda) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <h2 className="text-2xl font-bold text-slate-800 mb-4">Emenda não encontrada</h2>
        <Link to="/emendas" className="text-blue-600 font-bold flex items-center hover:underline">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar para lista
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      {/* Breadcrumb / Top Bar */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm font-medium text-slate-500">
            <Link to="/" className="hover:text-blue-600">Início</Link>
            <ChevronRight className="w-4 h-4 mx-2 text-slate-300" />
            <Link to="/emendas" className="hover:text-blue-600">Emendas</Link>
            <ChevronRight className="w-4 h-4 mx-2 text-slate-300" />
            <span className="text-slate-900 truncate max-w-[200px] md:max-w-md">{emenda.titulo}</span>
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">

          {/* Lado Esquerdo: Imagem e Informações Rápidas */}
          <div className="lg:col-span-4 space-y-8">
            {/* Card da Foto (Dedicado para não esticar) */}
            <div className="bg-white rounded-[32px] overflow-hidden shadow-sm border border-slate-100 p-4">
              <div className="aspect-square rounded-[24px] overflow-hidden bg-slate-100 relative">
                {emenda.imagem_capa ? (
                  <img
                    src={emenda.imagem_capa}
                    alt={emenda.titulo}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                    <FileText className="w-16 h-16 mb-2 opacity-20" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Sem Imagem</span>
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {emenda.categoria && (
                  <span className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider">
                    <Tag className="w-3 h-3 mr-1.5" />
                    {emenda.categoria}
                  </span>
                )}
                <button className="p-2 ml-auto text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Card de Atributos */}
            <div className="bg-white rounded-[32px] p-8 shadow-sm border border-slate-100 space-y-6">
              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-50 pb-4">Detalhes do Investimento</h3>

              {emenda.valor_emenda && (
                <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest block mb-1">Valor Total</span>
                  <span className="text-2xl font-black text-emerald-700">{formatarMoeda(emenda.valor_emenda)}</span>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center mr-4 shrink-0">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Local Beneficiado</span>
                    <span className="text-sm font-bold text-slate-700">{emenda.municipio_beneficiado || 'Região'} - {emenda.estado || 'BR'}</span>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center mr-4 shrink-0">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Data de Publicação</span>
                    <span className="text-sm font-bold text-slate-700">{formatarData(emenda.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lado Direito: Conteúdo e Documentos */}
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-white rounded-[32px] p-8 md:p-12 shadow-sm border border-slate-100">
              <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-6 leading-tight">
                {emenda.titulo}
              </h1>

              <div className="prose prose-slate prose-lg max-w-none">
                {emenda.resumo && (
                  <p className="text-slate-600 font-medium leading-relaxed italic border-l-4 border-blue-200 pl-6 mb-8">
                    {emenda.resumo}
                  </p>
                )}

                <div className="text-slate-700 leading-loose whitespace-pre-wrap">
                  {emenda.descricao_completa || 'Não há informações adicionais disponíveis para esta emenda.'}
                </div>
              </div>
            </div>

            {/* Seção de Documentos */}
            {documentos.length > 0 && (
              <div className="bg-white rounded-[32px] p-8 md:p-12 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center mr-4">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Documentos Oficiais</h2>
                      <p className="text-sm text-slate-500">Arquivos disponíveis para consulta pública</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {documentos.map((doc) => (
                    <a
                      key={doc.id}
                      href={doc.arquivo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-5 bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-100 rounded-2xl transition-all group"
                    >
                      <div className="flex items-center overflow-hidden mr-4">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-4 border border-slate-200 shadow-xs shrink-0">
                          <Download className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                        <span className="text-sm font-bold text-slate-700 group-hover:text-blue-900 truncate">
                          {doc.nome_documento}
                        </span>
                      </div>
                      <span className="text-[10px] font-black uppercase text-blue-600 bg-blue-100/50 px-2 py-1 rounded-md shrink-0">PDF</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-8">
              <Link
                to="/emendas"
                className="inline-flex items-center px-8 py-4 bg-slate-900 text-white font-bold rounded-2xl hover:bg-blue-600 transition-all active:scale-95 shadow-xl shadow-slate-200"
              >
                <ArrowLeft className="w-5 h-5 mr-3" />
                Voltar todas Emendas
              </Link>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default EmendaDetalhes;
